import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, UserCheck, Mail, Calendar, Hash } from "lucide-react";
import Avatar from "@/components/Avatar";

export default function PendingPlayers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isCoach = user?.role === "coach" || user?.role === "admin";

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/players/pending");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCoach) load();
  }, [isCoach]);

  const approve = async (id) => {
    try {
      await api.post(`/players/${id}/approve`);
      toast.success("Joueur validé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const reject = async (id, name) => {
    if (!confirm(`Refuser et supprimer le compte de ${name} ?`)) return;
    try {
      await api.post(`/players/${id}/reject`);
      toast.success("Compte refusé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  if (!isCoach) {
    return (
      <div className="text-slate-400 text-sm font-mono-stat uppercase">
        Accès réservé au staff.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-[#E5B85C] mb-2">
          Modération
        </div>
        <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight flex items-center gap-3">
          <UserCheck className="w-8 h-8 sm:w-10 sm:h-10 text-[#E5B85C]" />
          Inscriptions en attente
        </h1>
        <p className="text-sm text-slate-400 mt-2">
          Valide ou refuse les demandes d'inscription pour rejoindre le club.
        </p>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : items.length === 0 ? (
        <div
          data-testid="pending-empty"
          className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500"
        >
          Aucune inscription en attente 🎉
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`pending-row-${p.id}`}
              className="rounded-2xl bg-slate-900 border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <Avatar player={p} size={48} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg uppercase truncate">
                  {p.name}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-slate-400 font-mono-stat mt-1">
                  {p.email && (
                    <span className="flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3" /> {p.email}
                    </span>
                  )}
                  {p.jersey_number && (
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" /> {p.jersey_number}
                    </span>
                  )}
                  {p.created_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(p.created_at).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 sm:justify-end">
                <button
                  data-testid={`approve-${p.id}`}
                  onClick={() => approve(p.id)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold text-xs uppercase tracking-wide"
                >
                  <Check className="w-4 h-4" /> Valider
                </button>
                <button
                  data-testid={`reject-${p.id}`}
                  onClick={() => reject(p.id, p.name)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-300 hover:text-rose-200 font-bold text-xs uppercase tracking-wide"
                >
                  <X className="w-4 h-4" /> Refuser
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
